export type QrOrderingStatus = 'paused' | 'stopped';

export type QrOrderingDuration = '30m' | '60m' | 'manual';

export interface QrOrderingNotice {
  version: typeof QR_ORDERING_NOTICE_VERSION;
  status: QrOrderingStatus;
  duration: QrOrderingDuration;
  issuedAt: number;
  resumeAt: number | null;
}

export const QR_ORDERING_NOTICE_VERSION = 1 as const;

const DURATION_MINUTES: Record<QrOrderingDuration, number | null> = {
  '30m': 30,
  '60m': 60,
  manual: null,
};

const toEpochMillis = (value: number, fallback: number): number => {
  if (Number.isFinite(value)) {
    return Math.max(0, Math.trunc(value));
  }
  return fallback;
};

export const buildQrOrderingNotice = (input: {
  status: QrOrderingStatus;
  duration: QrOrderingDuration;
  issuedAt?: number;
}): QrOrderingNotice => {
  const issuedAt = toEpochMillis(input.issuedAt ?? Date.now(), Date.now());
  const durationMinutes = DURATION_MINUTES[input.duration];
  const resumeAt =
    typeof durationMinutes === 'number' ? issuedAt + durationMinutes * 60_000 : null;
  return {
    version: QR_ORDERING_NOTICE_VERSION,
    status: input.status,
    duration: input.duration,
    issuedAt,
    resumeAt,
  };
};

export const serializeQrOrderingNotice = (notice: QrOrderingNotice): string => {
  return JSON.stringify(notice);
};

const isQrOrderingStatus = (value: unknown): value is QrOrderingStatus => {
  return value === 'paused' || value === 'stopped';
};

const isQrOrderingDuration = (value: unknown): value is QrOrderingDuration => {
  return value === '30m' || value === '60m' || value === 'manual';
};

export const parseQrOrderingNotice = (input: unknown): QrOrderingNotice | null => {
  if (!input) {
    return null;
  }
  let record: unknown = input;
  if (typeof input === 'string') {
    try {
      record = JSON.parse(input);
    } catch {
      return null;
    }
  }
  if (!record || typeof record !== 'object') {
    return null;
  }
  const value = record as Record<string, unknown>;
  const versionRaw = typeof value.version === 'number' ? value.version : Number(value.version);
  if (versionRaw !== QR_ORDERING_NOTICE_VERSION) {
    return null;
  }
  if (!isQrOrderingStatus(value.status)) {
    return null;
  }
  if (!isQrOrderingDuration(value.duration)) {
    return null;
  }
  const issuedAt =
    typeof value.issuedAt === 'number' && Number.isFinite(value.issuedAt)
      ? Math.trunc(value.issuedAt)
      : Date.now();
  const resumeAt =
    typeof value.resumeAt === 'number' && Number.isFinite(value.resumeAt)
      ? Math.trunc(value.resumeAt)
      : null;

  return {
    version: QR_ORDERING_NOTICE_VERSION,
    status: value.status,
    duration: value.duration,
    issuedAt,
    resumeAt,
  };
};

export interface QrOrderingNoticeDisplay {
  status: QrOrderingStatus;
  duration: QrOrderingDuration;
  issuedAt: number;
  resumeAt: number | null;
  minutesRemaining: number | null;
  expired: boolean;
}

export const getQrOrderingNoticeDisplay = (
  notice: QrOrderingNotice,
  now: number = Date.now(),
): QrOrderingNoticeDisplay => {
  const resumeAt = notice.resumeAt;
  const minutesRemaining =
    typeof resumeAt === 'number'
      ? Math.max(0, Math.round((resumeAt - now) / 60_000))
      : null;
  const expired = typeof resumeAt === 'number' ? resumeAt <= now : false;
  return {
    status: notice.status,
    duration: notice.duration,
    issuedAt: notice.issuedAt,
    resumeAt,
    minutesRemaining,
    expired,
  };
};

export const getQrOrderingDurationMinutes = (
  duration: QrOrderingDuration,
): number | null => {
  return DURATION_MINUTES[duration];
};

export const formatEnglishQrOrderingNotice = (
  notice: QrOrderingNotice,
  now: number = Date.now(),
): string => {
  const prefix =
    notice.status === 'paused'
      ? 'QR ordering is temporarily paused'
      : 'QR ordering is currently offline';

  if (notice.duration === 'manual' || !notice.resumeAt) {
    return `${prefix}. Please order at the counter.`;
  }

  const minutesRemaining = Math.max(0, Math.round((notice.resumeAt - now) / 60_000));

  if (notice.duration === '30m' || minutesRemaining <= 60) {
    if (minutesRemaining <= 0) {
      return `${prefix}. Please check with the team for the current wait time.`;
    }
    return `${prefix} for the next 30 minutes. Please order at the counter.`;
  }

  if (minutesRemaining <= 0) {
    return `${prefix}. Please check with the team for the current wait time.`;
  }

  return `${prefix} for the next hour. Please order at the counter.`;
};
